import { Modal, Table, Button, Divider } from 'antd';
import { useSelector } from 'react-redux';
import { StepBackwardOutlined } from '@ant-design/icons';
import { fetchAccountByNumber } from '../../utils/accountStructure/accountStructureUtils';

import Localization from '../../utils/localization';
import './styles.scss';

const AuditTrail = ({
  expandedPeriods, // unused but kept
  periods,
  auditTrailData,
  handleSetAuditTrailData,
  handleSetPreviousAuditTrailData,
  accountStructure,
  scenario,
  // businessCase = { source: assembledBC, scenarios: [{...parsedScenario, name}] }
  // When provided, the AuditTrail starts in BC mode. Each entry on the
  // auditTrailData stack may carry its own context to support drilling from
  // a BC scenario row down into that single scenario without losing the BC frame.
  businessCase,
  isAuditTrailOpen,
  setIsAuditTrailOpen,
}) => {
  // Org-level formatting settings (notation + per-metric decimals).
  // Must be called before any conditional return to satisfy Rules of Hooks.
  const organization = useSelector((state) => state.organization.organization);

  // ---------- basic guards ----------
  if (!auditTrailData || auditTrailData.length === 0) {
    return null;
  }

  const currentData = auditTrailData[auditTrailData.length - 1];
  if (!currentData || !currentData.account || !currentData.period) {
    return null;
  }

  const account = currentData.account;
  const period = currentData.period;

  // ---------- context resolution ----------
  // Each entry on the auditTrailData stack may carry its own `context`:
  //   { kind: 'businessCase', source, scenarios } | { kind: 'scenario', source }
  // If a stack frame doesn't specify one, fall back to the BC prop if present,
  // else the scenario prop. This keeps the component backward-compatible with
  // existing scenario-only call sites.
  const resolveContext = () => {
    if (currentData.context) return currentData.context;
    if (businessCase) {
      return {
        kind: 'businessCase',
        source: businessCase.source,
        scenarios: businessCase.scenarios || [],
      };
    }
    return { kind: 'scenario', source: scenario };
  };

  const context = resolveContext();
  const isBc = context.kind === 'businessCase';

  // The Values map for the current context (BC combined or single scenario).
  const contextValues = context.source && context.source.values;

  // Read a cell from any Values map (Map<accNumber, Value[]>).
  const readCell = (values, accNumber, periodId) => {
    if (!values || !accNumber) return null;
    const arr = values.get(accNumber) || [];
    const idx = arr.findIndex((v) => v && v.period === periodId);
    return idx >= 0 ? arr[idx] : null;
  };

  // Map the org "notation" setting to a BCP-47 locale.
  // American → en-US, European → da-DK. Falls back to runtime locale if unset.
  const localeFromNotation = (notation) => {
    if (notation === 'American') return 'en-US';
    if (notation === 'European') return 'da-DK';
    return undefined;
  };

  // Pick the right decimals count for an account based on its metric flags.
  // Defaults to 2 if the org isn't loaded yet or no flag is set.
  const decimalsForAccount = (acc) => {
    const org = organization;
    if (!org || !acc) return 2;
    if (acc.ratio) return org.ratioDecimals ?? 2;
    if (acc.unit) return org.unitDecimals ?? 2;
    // numeric is the default for accounts where no other flag is set
    return org.numericDecimals ?? 2;
  };

  // Format a raw value with the given decimals + the org locale.
  // Non-numeric strings are passed through untouched.
  const formatValue = (raw, decimals) => {
    if (raw == null || raw === '') return '';
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(num)) return raw;
    return new Intl.NumberFormat(localeFromNotation(organization?.notation), {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // ---------- helpers ----------

  const formulaIndex = (acc, periodId) => {
    if (!acc || !Array.isArray(acc.formulas)) return -1;
    const idx = acc.formulas.findIndex((f) => f && f.period === periodId);
    return idx >= 0 ? idx : -1;
  };

  // Detect rollup: account has a non-empty children array of child Account objects.
  const isParentAccount = (acc) =>
    !!acc && Array.isArray(acc.children) && acc.children.length > 0;

  // Build the synthetic "Σ a + b + c" formula string shown for rollup parents.
  const buildRollupFormula = (acc) => {
    if (!isParentAccount(acc)) return '';
    const parts = (acc.children || [])
      .map((c) => c && c.account_number)
      .filter(Boolean);
    return parts.length ? 'Σ ' + parts.join(' + ') : '';
  };

  // Drilling is allowed if the row is a rollup parent (we'll show its children),
  // a scenario row in BC mode (drilling pivots context into that scenario),
  // or the cell has formula refs.
  const canDrill = (row) => {
    if (!row || !row.account || !row.period) return false;
    if (row.isScenarioRow) return true;
    if (row.isParent) return true;
    return Array.isArray(row.refs) && row.refs.length > 0;
  };

  // ---------- mother row (the clicked cell) ----------

  const createMotherDatasource = (acc, periodId) => {
    if (!acc || !acc.account_number || !contextValues) {
      return [];
    }

    const values = contextValues.get(acc.account_number) || [];
    const idx = formulaIndex(acc, periodId);
    const cell = values[idx];
    const parent = isParentAccount(acc);

    return [
      {
        key: acc.account_id || (acc.account_number + '-' + periodId),
        account: acc.account_number,
        accountNumber: acc.account_number,
        name: acc.name || '',
        period: periodId,
        formula: parent
          ? buildRollupFormula(acc)
          : (acc.formulas && acc.formulas[idx] && acc.formulas[idx].formula) || '',
        value: cell && cell.formula != null ? cell.formula : '',
        isNA: !!(cell && cell.isNA),
        naReasons: (cell && cell.naReasons) || [],
        refs: (cell && cell.refs) || [],
        isParent: parent,
        decimals: decimalsForAccount(acc),
      },
    ];
  };

  const columns = [
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      width: 120,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 290,
    },
    {
      title: 'Formula',
      dataIndex: 'formula',
      key: 'formula',
      width: 290,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (_, row) =>
        row.isNA ? (
          <span style={{ color: 'red' }}>NaN</span>
        ) : (
          formatValue(row.value, row.decimals ?? 2)
        ),
    },
  ];

  // Mother-row columns: drop Formula when it doesn't apply at the BC level.
const motherIsParent = isParentAccount(account);
const motherCombine = account.bc_combine || 'sum';
const formulaMeaningful = !isBc || motherCombine === 'derive';
const motherColumns = formulaMeaningful
  ? columns
  : columns.filter((c) => c.key !== 'formula');

// Contributing-scenarios columns: explicit, no Formula, Value visible.
const bcScenarioColumns = [
   { title: 'Entity',   dataIndex: 'entityName', key: 'entityName', width: 160,
    render: (v) => v || '—' },
  { title: 'Scenario', dataIndex: 'account', key: 'account', width: 150 },
  { title: 'Name',     dataIndex: 'name',    key: 'name',    width: 200 },
  {
    title: 'Combine', key: 'combine', width: 100,
    render: (_, row) => {
      if (row.combine === 'weighted_avg') {
        const wText = Number.isFinite(row.weight)
          ? formatValue(row.weight, row.decimals ?? 2)
          : '—';
        return `weighted (w=${wText})`;
      }
      if (row.combine === 'derive') return 'derive';
      return 'sum';
    },
  },
  {
    title: 'Value', dataIndex: 'value', key: 'value', align: 'right',
    render: (_, row) =>
      row.isNA
        ? <span style={{ color: 'red' }}>NaN</span>
        : formatValue(row.value, row.decimals ?? 2),
  },
];
  // ---------- period offset label helpers ----------

  const getPeriodIndex = (periodId) => {
    if (!Array.isArray(periods)) return -1;
    return periods.findIndex(
      (p) =>
        p.id === periodId ||
        p.period === periodId ||
        p.period_id === periodId
    );
  };

  const buildAccountLabel = (accNumber, parentPeriodId, childPeriodId) => {
    const parentIdx = getPeriodIndex(parentPeriodId);
    const childIdx = getPeriodIndex(childPeriodId);

    if (parentIdx >= 0 && childIdx >= 0) {
      const offset = childIdx - parentIdx;
      if (offset !== 0) {
        return `${accNumber}[${offset}]`;
      }
    }
    return accNumber;
  };

  // ---------- child rows ----------
  // Rollup parents: rollup edges in ScenarioBuildService go child → parent,
  // which means the parent's own directRefs is empty. So for rollups we walk
  // acc.children directly at the same period as the parent.
  // Formula accounts: walk cell.refs as before.

  const createRollupChildren = (acc, periodId) => {
    if (!acc || !Array.isArray(acc.children)) return [];

    return acc.children
      .map((child, i) => {
        if (!child || !child.account_number) return null;

        const childAccNumber = child.account_number;
        const childCell = readCell(contextValues, childAccNumber, periodId);
        const childValues = (contextValues && contextValues.get(childAccNumber)) || [];
        const childIdx = childValues.findIndex(
          (v) => v && v.period === periodId
        );
        const childIsParent = isParentAccount(child);

        return {
          key:
            child.account_id ||
            childAccNumber + '-' + periodId + '-rollup-' + i,
          account: childAccNumber, // same period as parent — no offset suffix
          accountNumber: childAccNumber,
          name: child.name || '',
          period: periodId,
          formula: childIsParent
            ? buildRollupFormula(child)
            : (child.formulas &&
                child.formulas[childIdx] &&
                child.formulas[childIdx].formula) ||
              '',
          value: childCell && childCell.formula != null ? childCell.formula : '',
          isNA: !!(childCell && childCell.isNA),
          naReasons: (childCell && childCell.naReasons) || [],
          // For drilling: a non-parent child uses its own refs; a parent child
          // will be re-detected as a rollup on the next drill via isParent.
          refs: (childCell && childCell.refs) || [],
          isParent: childIsParent,
          decimals: decimalsForAccount(child),
        };
      })
      .filter(Boolean);
  };

  // ---------- BC: contributing scenarios ----------
  // For an account in a BC where bc_combine !== 'derive', the breakdown is
  // one row per contributing scenario, showing each scenario's value plus
  // (when relevant) weight and contribution math.
  const createBcContributingScenarios = (acc, periodId) => {
    if (!acc || !acc.account_number) return [];
    const scenarios = (context && context.scenarios) || [];
    if (scenarios.length === 0) return [];

    const combine = acc.bc_combine || 'sum';
    const weightAccNumber = acc.bc_weight;

    // For weighted_avg, pre-compute Σ weights so we can show each row's
    // contribution as (v * w) / Σw.
    let weightTotal = 0;
    if (combine === 'weighted_avg' && weightAccNumber) {
      for (const s of scenarios) {
        const wCell = readCell(s.values, weightAccNumber, periodId);
        const w = wCell ? Number(wCell.result ?? wCell.formula ?? 0) : 0;
        if (Number.isFinite(w)) weightTotal += w;
      }
    }

    return scenarios.map((s, i) => {
      const cell = readCell(s.values, acc.account_number, periodId);
      const v = cell ? Number(cell.result ?? cell.formula ?? 0) : 0;

      let weight;
      let contribution;
      if (combine === 'weighted_avg' && weightAccNumber) {
        const wCell = readCell(s.values, weightAccNumber, periodId);
        weight = wCell ? Number(wCell.result ?? wCell.formula ?? 0) : 0;
        contribution =
          weightTotal !== 0 && Number.isFinite(weightTotal)
            ? (v * weight) / weightTotal
            : null;
      }

      const displayName = s.name || `Scenario ${s.scenario_id}`;

      return {
        key: 'bc-scenario-' + s.scenario_id + '-' + i,
        // The "account" column is repurposed to show the scenario name here.
        account: displayName,
        accountNumber: acc.account_number,
        name: acc.name || '',
        period: periodId,
        formula: '', // not meaningful per-scenario at this level
        value: cell && cell.formula != null ? cell.formula : v,
        isNA: !!(cell && cell.isNA),
        naReasons: (cell && cell.naReasons) || [],
        // refs not used for drilling here — drilling pivots context instead
        refs: [],
        isParent: false,
        decimals: decimalsForAccount(acc),
        // BC-specific extras
        isScenarioRow: true,
        scenarioId: s.scenario_id,
        scenarioName: displayName,
        scenarioObj: s,
        entityName: s.entityName ?? null,
        combine,
        weight,
        weightAccNumber,
        contribution,
      };
    });
  };

  const createChildDataSource = (acc, periodId, scenario, accountStructure) => {
    if (!acc || !acc.account_number) return [];

    // Rollup branch: behavior depends on context.
  // - BC context: show per-scenario rollup totals (drill into a scenario for children)
  // - Scenario context: show children directly
  if (isParentAccount(acc)) {
    if (isBc && (context.scenarios || []).length > 0) {
      return createBcContributingScenarios(acc, periodId);
    }
    return createRollupChildren(acc, periodId);
  }

     // BC, non-rollup, non-derive → contributing scenarios (unchanged)
  if (isBc && (acc.bc_combine || 'sum') !== 'derive') {
    return createBcContributingScenarios(acc, periodId);
  }

    // Otherwise: formula-refs branch (scenario mode, or BC + derive).
    // Reads from contextValues so derive accounts in BC mode resolve refs
    // against the BC's combined values, which is correct since derive formulas
    // are evaluated at the BC level in compileExcludedAccounts.
    if (!contextValues) return [];

    const values = contextValues.get(acc.account_number) || [];
    const idx = formulaIndex(acc, periodId);
    const cell = values[idx];
    const refs = (cell && cell.refs) || [];

    const childDataSource = [];

    refs.forEach((ref, i) => {
      try {
        if (!ref || !ref.accNumber || !ref.periodId) return;

        const childAccNumber = ref.accNumber;
        const childPeriodId = ref.periodId;

        const wrapper = {};
        fetchAccountByNumber(childAccNumber, wrapper, accountStructure);
        const childAcc = wrapper.account;

        const childValues = contextValues.get(childAccNumber) || [];
        const childIdx = childValues.findIndex(
          (v) => v && v.period === childPeriodId
        );
        const childCell = childIdx >= 0 ? childValues[childIdx] : null;
        const childIsParent = isParentAccount(childAcc);

        let accountLabel;
        if (ref.offsetLabel) {
          accountLabel = `${childAccNumber}${ref.offsetLabel}`;
        } else {
          accountLabel = buildAccountLabel(
            childAccNumber,
            periodId,
            childPeriodId
          );
        }

        childDataSource.push({
          key:
            (childAcc && childAcc.account_id) ||
            childAccNumber + '-' + childPeriodId + '-' + i,
          account: accountLabel,
          accountNumber: childAccNumber,
          name: (childAcc && childAcc.name) || '',
          period: childPeriodId,
          formula: childIsParent
            ? buildRollupFormula(childAcc)
            : (childAcc &&
                childAcc.formulas &&
                childAcc.formulas[childIdx] &&
                childAcc.formulas[childIdx].formula) ||
              '',
          value:
            childCell && childCell.formula != null ? childCell.formula : '',
          isNA: !!(childCell && childCell.isNA),
          naReasons: (childCell && childCell.naReasons) || [],
          refs: (childCell && childCell.refs) || [],
          isParent: childIsParent,
          decimals: decimalsForAccount(childAcc),
        });
      } catch (err) {
        childDataSource.push({
          key: 'error-' + i,
          account: '(error)',
          name: '',
          period: '',
          formula: '',
          value: '',
          isNA: true,
          naReasons: [
            {
              kind: 'OTHER',
              message: 'Failed to resolve reference for child #' + i,
            },
          ],
          refs: [],
          isParent: false,
        });
      }
    });

    return childDataSource;
  };

  // ---------- NA reason rendering ----------

  const renderNaReasons = (row) => {
    const reasons = (row && row.naReasons) || [];
    if (!row || !row.isNA || reasons.length === 0) return null;

    return (
      <>
        <Divider plain>Why this is NaN</Divider>
        <ul className="audittrail-na-reasons">
          {reasons.map((r, idx) => (
            <li key={idx}>
              {r.kind === 'OUT_OF_RANGE_REF' && (
                <>
                  Reference <code>{r.ref}</code> points to a period that does not
                  exist
                  {r.targetPeriod ? ' (' + r.targetPeriod + ')' : ''}.
                </>
              )}
              {r.kind === 'SOURCE_NA' && (
                <>{r.message || 'Depends on another cell that is NaN/invalid.'}</>
              )}
              {r.kind === 'DIV_BY_ZERO' && <>Division by zero in formula.</>}
              {r.kind === 'OTHER' && <>{r.message || 'Unknown reason.'}</>}
              {!r.kind && <>{r.message || 'Unknown reason.'}</>}
            </li>
          ))}
        </ul>
      </>
    );
  };

  const handleCancel = () => {
    setIsAuditTrailOpen(false);
  };

  const renderAuditTrail = () => {
    if (!currentData) return null;

    const mother = createMotherDatasource(account, period);
    const children = createChildDataSource(
      account,
      period,
      scenario,
      accountStructure
    );
    const motherRow = mother[0];

    // The contributing-scenarios table needs an extra "Combine" column.
    // Other tables (mother, rollup, formula refs) keep the original 4 columns.
    
    const childIsScenarioRows =
        isBc && children.length > 0 && children[0].isScenarioRow;
    const showCombineColumn =
      childIsScenarioRows && !motherRow?.isParent;

    const bcScenarioColumnsNoCombine =
      bcScenarioColumns.filter(c => c.key !== 'combine');

    const childColumns = childIsScenarioRows
    ? (motherRow?.isParent ? bcScenarioColumnsNoCombine : bcScenarioColumns)
    : columns;


    // Divider label for the lower section.
    const lowerDividerLabel = (() => {
      if (!motherRow) return 'References';
      if (childIsScenarioRows) {
        if (!motherRow.isParent) {
          const combine = (account.bc_combine || 'sum');
          if (combine === 'weighted_avg' && account.bc_weight) {
            return `Contributing scenarios (weighted by ${account.bc_weight})`;
          }
        }
        return 'Contributing scenarios';
          }
      if (motherRow.isParent) return 'Contributing accounts';
  return 'References';
    })();

    // Routing for double-click on a child row.
    const handleChildDrill = (record) => {
      if (!canDrill(record)) return;
      // Use the raw account number (without [-n] period offsets) for lookup.
      const drillAccount = record.accountNumber || record.account;
      if (record.isScenarioRow) {
        // Pivot context: drill INTO that scenario, keeping the same account+period.
        const nextContext = {
          kind: 'scenario',
          source: record.scenarioObj,
        };
        handleSetAuditTrailData(drillAccount, record.period, nextContext);
      } else {
        handleSetAuditTrailData(drillAccount, record.period);
      }
    };

    return (
      <>
        {/* Pinned header area: back button, mother row, NaN reasons, divider */}
        <div style={{ flex: '0 0 auto' }}>
          <Button
            onClick={handleSetPreviousAuditTrailData}
            type="primary"
            icon={<StepBackwardOutlined />}
          />
          <Divider plain />
          
          
          
          <Table
            dataSource={mother}
            pagination={false}
            columns={motherColumns}
            rowKey="key"
            onRow={(record) => ({
              onDoubleClick: () => {
                if (canDrill(record)) {
                  handleSetAuditTrailData(
                    record.accountNumber || record.account,
                    record.period
                  );
                }
              },
            })}
          />

          {motherRow && renderNaReasons(motherRow)}
          <Divider plain>{lowerDividerLabel}</Divider>
        </div>

        {/* Scrollable contributing area */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <Table
            dataSource={children}
            pagination={false}
            columns={childColumns}
            rowKey="key"
            sticky
            onRow={(record) => ({
              onDoubleClick: () => handleChildDrill(record),
            })}
          />
        </div>
      </>
    );
  };

  return (
    <Modal
      bodyStyle={{
        minHeight: '30vh',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      title={Localization.scenario.audit_trail}
      visible={isAuditTrailOpen}
      confirmLoading={false}
      onCancel={handleCancel}
      footer={null}
      width={900}
    >
      {renderAuditTrail()}
    </Modal>
  );
};

export default AuditTrail;
